import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Plus, Search as SearchIcon } from "lucide-react";
import { Badge, Button, Pagination, Select, statusTone, Tabs } from "../../components/ui";
import { useApi } from "../../api/client";
import { Competition } from "../../data/types";

export default function CompetitionList() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("All Competitions");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All Status");
  const { data: competitions } = useApi<Competition[]>("/competitions", []);
  const q = search.trim().toLowerCase();
  const filtered = competitions.filter((c) => {
    if (tab === "Upcoming" && c.status !== "UPCOMING") return false;
    if (tab === "Live Now" && c.status !== "LIVE") return false;
    if (tab === "Completed" && c.status !== "COMPLETED") return false;
    if (tab === "Archived" && c.status !== "ARCHIVED") return false;
    if (status !== "All Status" && c.status !== status.toUpperCase()) return false;
    if (q && !`${c.name} ${c.city} ${c.range}`.toLowerCase().includes(q)) return false;
    return true;
  });

  return (
    <div className="mx-auto max-w-[1400px] px-5 py-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink">Events</h1>
          <p className="mt-1 text-[13px] text-muted">Browse and view shooting competitions and events.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-64">
            <SearchIcon size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className="input pl-9" placeholder="Search competitions..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <Select options={["All Status", "Live", "Completed", "Upcoming", "Archived"]} value={status} onChange={setStatus} className="w-full sm:w-36" />
          <Select options={["All Years", "2024", "2023"]} className="w-full sm:w-32" />
          <Button>
            <Plus size={16} /> Add / Create Competition
          </Button>
        </div>
      </div>

      <Tabs
        className="mt-8"
        tabs={["All Competitions", "Upcoming", "Live Now", "Completed", "Archived"]}
        active={tab}
        onChange={setTab}
      />

      <div className="card mt-4">
        <div className="table-scroll [--table-offset:360px]">
          <table className="w-full min-w-[1040px]">
          <thead>
            <tr className="border-b border-line">
              <th className="th">Event</th>
              <th className="th">Dates</th>
              <th className="th">Location</th>
              <th className="th">Status</th>
              <th className="th text-center">Competitions</th>
              <th className="th text-center">Matches</th>
              <th className="th text-center">Shooters</th>
              <th className="th text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {!filtered.length && (
              <tr>
                <td colSpan={8} className="td py-10 text-center text-muted">
                  No competitions yet — import a shoot workbook to populate the portal.
                </td>
              </tr>
            )}
            {filtered.map((c) => (
              <tr key={c.id} onClick={() => navigate(`/competitions/${c.id}`)} className="cursor-pointer transition-colors hover:bg-slate-50">
                <td className="td">
                  <Link to={`/competitions/${c.id}`} className="font-bold text-ink hover:text-primary">
                    {c.name}
                  </Link>
                </td>
                <td className="td whitespace-nowrap">
                  {c.startDate}
                  {c.endDate && c.endDate !== c.startDate ? ` – ${c.endDate}` : ""}
                  {c.days > 1 && <span className="text-muted"> · {c.days} days</span>}
                </td>
                <td className="td text-slate-600">
                  {[c.range || c.city, c.stateCode, c.country].filter(Boolean).join(", ")}
                </td>
                <td className="td">
                  {/* What was actually recorded, on the same line as the badge it
                      qualifies rather than wrapping the row onto a third. */}
                  <span className="flex items-center gap-2 whitespace-nowrap">
                    <Badge tone={statusTone(c.status)}>{c.status}</Badge>
                    {c.statusDetail && <span className="text-[12px] text-muted">{c.statusDetail}</span>}
                  </span>
                </td>
                <td className="td text-center font-semibold">{c.competitions ?? 1}</td>
                <td className="td text-center font-semibold">{c.matches || "—"}</td>
                <td className="td text-center font-semibold">{c.shooters || "—"}</td>
                <td className="td text-right text-muted">
                  {/* The whole row opens the event; this is the affordance, not a
                      second button the height of three lines of text. */}
                  <ChevronRight size={16} className="inline" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <div className="border-t border-line">
          <Pagination summary={`${filtered.length} ${filtered.length === 1 ? "competition" : "competitions"}`} />
        </div>
      </div>
    </div>
  );
}
